import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, FileText, Loader2 } from "lucide-react";
import {
  generatableAcordForms,
  type AcordFormDefinition,
  type AcordFormId,
} from "@shared/acord-forms";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  acordFormPrefillQueryKey,
  fetchAcordFormPrefill,
  useInsuredPolicies,
  type AcordFormPrefill,
  type AcordPrefillRequest,
} from "@/lib/api";
import {
  acordPendingRequirementsMessage,
  acordPolicyRestrictionHint,
  acordPolicySectionHint,
  acordPolicySectionLabel,
  acordPolicyUnavailableReason,
  acordSelectionCoveredByForm,
  resolveAcordSelectionRequirements,
  type AcordExtraValues,
} from "./acord-form-selection";

// What the editor modal (CSR-2063) needs to open: the selection that produced the
// document, plus the values it opens with.
export interface AcordFormHandoff {
  form: AcordFormDefinition;
  request: AcordPrefillRequest;
  prefill: AcordFormPrefill;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insuredId: string;
  onGenerated: (handoff: AcordFormHandoff) => void;
}

// The prefill is a pure function of the selection, so the editor mounting a
// moment later must not re-request it. Long enough to cover the handoff and an
// agent stepping back and forth between the two modals.
const PREFILL_STALE_MS = 5 * 60 * 1000;

const FORMS = generatableAcordForms();

export function GenerateAcordFormDialog({
  open,
  onOpenChange,
  insuredId,
  onGenerated,
}: Props) {
  const queryClient = useQueryClient();
  // Every policy the insured has, any status — an agent legitimately generates a
  // Statement of No Loss for a cancelled policy, so this list is never filtered.
  const {
    data: policiesResponse,
    isPending: policiesPending,
    isError: policiesFailed,
  } = useInsuredPolicies(insuredId);
  const policies = policiesResponse?.data ?? [];

  const [formId, setFormId] = useState<AcordFormId | null>(null);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
  const [extras, setExtras] = useState<AcordExtraValues>({});
  const [isPreparing, setIsPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);

  const form = useMemo(
    () => FORMS.find((candidate) => candidate.id === formId) ?? null,
    [formId],
  );

  const selection = {
    form,
    policyCount: policies.length,
    selectedPolicyIds,
    extras,
  };
  const requirements = resolveAcordSelectionRequirements(selection);
  const pendingMessage = acordPendingRequirementsMessage(selection, requirements);

  // CSR-2331 — on a form covering one carrier and one effective date, the first
  // pick decides which other policies may join it. Those that may not stay listed
  // but disabled, each with its reason.
  const selectedPolicies = selectedPolicyIds.flatMap(
    (id) => policies.find((policy) => policy.id === id) ?? [],
  );
  const unavailableReasons = new Map(
    policies.map((policy) => [
      policy.id,
      acordPolicyUnavailableReason(form, policy, selectedPolicies),
    ]),
  );
  const somePolicyUnavailable = Array.from(unavailableReasons.values()).some(
    (reason) => reason !== null,
  );

  // Cancel/X leaves nothing behind — the next open starts clean.
  useEffect(() => {
    if (open) return;
    setFormId(null);
    setSelectedPolicyIds([]);
    setExtras({});
    setIsPreparing(false);
    setPrepareError(null);
  }, [open]);

  const handleFormChange = (nextId: string) => {
    const next = FORMS.find((candidate) => candidate.id === nextId);
    if (!next) return;
    setFormId(next.id);
    setPrepareError(null);
    // The policies carry over — they are the same policies — but a form with one
    // slot on the paper can only ever hold the first, and a form covering one
    // carrier and one effective date only those that share the first's.
    setSelectedPolicyIds((current) =>
      next.policySelection === "single"
        ? current.slice(0, 1)
        : acordSelectionCoveredByForm(next, current, policies),
    );
  };

  const togglePolicy = (policyId: string, checked: boolean) => {
    setSelectedPolicyIds((current) =>
      checked
        ? [...current.filter((id) => id !== policyId), policyId]
        : current.filter((id) => id !== policyId),
    );
  };

  const setExtra = (key: keyof AcordExtraValues, value: string) => {
    setExtras((current) => ({ ...current, [key]: value }));
  };

  // A policy list that has not arrived yet looks exactly like an insured with no
  // policies, and that is the one case where a policy-based form generates
  // without a policy. Wait for the answer rather than acting on the gap.
  const policyContextUnavailable =
    form !== null &&
    form.policySelection !== "none" &&
    (policiesPending || policiesFailed);

  const canGenerate =
    form !== null && requirements.canGenerate && !policyContextUnavailable && !isPreparing;

  const handleGenerate = async () => {
    if (!form || !canGenerate) return;

    const request: AcordPrefillRequest = {
      insuredId,
      formId: form.id,
      policyIds: requirements.asksForPolicies ? selectedPolicyIds : [],
      extras: Object.fromEntries(
        requirements.extraFields
          .map((field) => [field.key, extras[field.key]?.trim()] as const)
          .filter(([, value]) => Boolean(value)),
      ),
    };

    setIsPreparing(true);
    setPrepareError(null);
    try {
      // `fetchQuery` rather than a bare fetch: the result lands in the shared
      // cache under the selection's key, so CSR-2063's editor renders it without
      // a second round-trip.
      const prefill = await queryClient.fetchQuery({
        queryKey: acordFormPrefillQueryKey(request),
        queryFn: () => fetchAcordFormPrefill(request),
        staleTime: PREFILL_STALE_MS,
      });
      onGenerated({ form, request, prefill });
      onOpenChange(false);
    } catch (error) {
      console.error("ACORD form prefill failed:", error);
      setPrepareError(
        error instanceof Error && error.message
          ? error.message
          : "Failed to prepare the form. Please try again.",
      );
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isPreparing && onOpenChange(next)}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-generate-acord-form"
      >
        <DialogHeader>
          <DialogTitle>Generate ACORD Form</DialogTitle>
          <DialogDescription>
            Select a form to pre-fill with this insured's information. You can edit every
            field before finalizing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <RadioGroup
            value={formId ?? ""}
            onValueChange={handleFormChange}
            aria-label="ACORD form"
          >
            {FORMS.map((candidate) => (
              <Label
                key={candidate.id}
                htmlFor={`acord-form-${candidate.id}`}
                className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-3 text-sm font-normal transition-colors hover:bg-muted/50 ${
                  formId === candidate.id ? "border-primary bg-muted/50" : ""
                }`}
                data-testid={`option-acord-form-${candidate.id}`}
              >
                <RadioGroupItem
                  id={`acord-form-${candidate.id}`}
                  value={candidate.id}
                  disabled={isPreparing}
                  data-testid={`radio-acord-form-${candidate.id}`}
                />
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span>{candidate.label}</span>
              </Label>
            ))}
          </RadioGroup>

          {/* Sub-selections — which ones appear is the registry's answer, per form. */}
          {form !== null && policyContextUnavailable && (
            <p
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="acord-policies-unavailable"
              role="status"
            >
              {policiesFailed ? (
                "This insured's policies could not be loaded, so a policy cannot be selected. Close and try again."
              ) : (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Loading policies...
                </>
              )}
            </p>
          )}

          {requirements.asksForPolicies && form !== null && (
            <fieldset
              className={`space-y-2 rounded-md border p-3 ${
                requirements.missingPolicy ? "border-destructive/60" : ""
              }`}
              data-testid="acord-policy-selection"
              data-mode={requirements.policyMode}
            >
              <legend className="px-1 text-xs font-semibold tracking-wide text-muted-foreground">
                {acordPolicySectionLabel(requirements.policyMode)}{" "}
                <span className="font-normal">
                  {acordPolicySectionHint(requirements.policyMode, form.policyRequired)}
                </span>
              </legend>

              {requirements.policyMode === "single" ? (
                <RadioGroup
                  value={selectedPolicyIds[0] ?? ""}
                  onValueChange={(policyId) => setSelectedPolicyIds([policyId])}
                  aria-label="Policy"
                >
                  {policies.map((policy) => (
                    <Label
                      key={policy.id}
                      htmlFor={`acord-policy-${policy.id}`}
                      className="flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm font-normal hover:bg-muted/50"
                    >
                      <RadioGroupItem
                        id={`acord-policy-${policy.id}`}
                        value={policy.id}
                        disabled={isPreparing}
                        data-testid={`radio-acord-policy-${policy.id}`}
                      />
                      <PolicyOptionText policy={policy} />
                    </Label>
                  ))}
                </RadioGroup>
              ) : (
                <div className="grid gap-2">
                  {policies.map((policy) => {
                    const unavailableReason = unavailableReasons.get(policy.id) ?? null;
                    return (
                      <Label
                        key={policy.id}
                        htmlFor={`acord-policy-${policy.id}`}
                        className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm font-normal ${
                          unavailableReason
                            ? "cursor-not-allowed bg-muted/30 opacity-60"
                            : "cursor-pointer hover:bg-muted/50"
                        }`}
                      >
                        <Checkbox
                          id={`acord-policy-${policy.id}`}
                          checked={selectedPolicyIds.includes(policy.id)}
                          onCheckedChange={(checked) =>
                            togglePolicy(policy.id, checked === true)
                          }
                          disabled={isPreparing || unavailableReason !== null}
                          aria-describedby={
                            unavailableReason ? `acord-policy-unavailable-${policy.id}` : undefined
                          }
                          data-testid={`checkbox-acord-policy-${policy.id}`}
                        />
                        <span className="min-w-0">
                          <PolicyOptionText policy={policy} />
                          {unavailableReason && (
                            <span
                              id={`acord-policy-unavailable-${policy.id}`}
                              className="block text-xs text-muted-foreground"
                              data-testid={`acord-policy-unavailable-${policy.id}`}
                            >
                              {unavailableReason}
                            </span>
                          )}
                        </span>
                      </Label>
                    );
                  })}
                </div>
              )}

              {somePolicyUnavailable && (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="acord-policy-restriction-hint"
                >
                  {acordPolicyRestrictionHint(form)}
                </p>
              )}
            </fieldset>
          )}

          {requirements.extraFields.map((field) => (
            <div key={field.key} className="space-y-2">
              <Label htmlFor={`acord-extra-${field.key}`}>
                {field.label} <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`acord-extra-${field.key}`}
                // No min/max: a backdated cancellation is legitimate.
                type="date"
                value={extras[field.key] ?? ""}
                onChange={(event) => setExtra(field.key, event.target.value)}
                disabled={isPreparing}
                className={`cursor-pointer md:max-w-xs ${
                  requirements.missingExtraKeys.includes(field.key)
                    ? "border-destructive/60"
                    : ""
                }`}
                aria-invalid={requirements.missingExtraKeys.includes(field.key)}
                data-testid={`input-acord-extra-${field.key}`}
              />
            </div>
          ))}

          {prepareError && (
            <p
              className="text-sm text-destructive"
              role="alert"
              data-testid="error-acord-generate"
            >
              {prepareError}
            </p>
          )}
        </div>

        <DialogFooter className="sm:items-center">
          {pendingMessage && !isPreparing && (
            <p
              className="mr-auto text-sm text-muted-foreground"
              data-testid="acord-generate-hint"
            >
              {pendingMessage}
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPreparing}
            data-testid="button-cancel-generate-acord"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            // The spec's "Generate button state" matrix, in one expression — the
            // per-form part of it comes from the registry, not from here.
            disabled={!canGenerate}
            data-testid="button-generate-acord"
          >
            {isPreparing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Preparing...
              </>
            ) : (
              <>
                Generate
                <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// "[Policy Number] — [Carrier Name]  [Product Type]", per the design spec.
function PolicyOptionText({
  policy,
}: {
  policy: { policyNumber: string; carrier: string; type: string | null };
}) {
  return (
    <span className="min-w-0">
      <span className="break-words">
        {policy.policyNumber} — {policy.carrier}
      </span>
      {policy.type && (
        <span className="ml-2 text-muted-foreground">{policy.type}</span>
      )}
    </span>
  );
}
